<template>
  <SoDialog
    v-model="value"
    :card-title="isEdit ? 'Contact' : 'New Contact'"
    data-testid="contact-details-modal"
  >
    <SoAlert
      v-if="contact?.isOccupier"
      class="mb-4"
      data-testid="contact-details-modal--occupier-message"
    >
      <p>
        You <strong>cannot edit contact details</strong> on an Occupier Account
        here.
      </p>
      <p>
        Go to the
        <SoLink @click="goToCot">
          <strong>Moving Home tool</strong>
        </SoLink>
        in Nova and Process <strong>Move in for the new customer</strong>.
      </p>
    </SoAlert>
    <SoAlert
      v-if="isProtectedContact"
      class="mb-4"
      data-testid="contact-details-modal--protected-message"
    >
      <p>This contact <strong>cannot be edited</strong> from here.</p>
      <p>
        Use the <strong>Manage bill delivery methods</strong> modal in the
        sidebar (or <strong>Priority Services</strong> for PSR-managed paper
        bills) to remove it.
      </p>
    </SoAlert>
    <SoAlert
      v-if="showReservedEmailError"
      class="mb-4"
      type="error"
      data-testid="contact-details-modal--reserved-email-error"
      title="This email is reserved for paper bill delivery. Use the Manage bill delivery methods modal in the sidebar (or Priority Services for PSR-managed paper bills) instead."
    />
    <SoErrorBoundary>
      <SoForm
        ref="form"
        data-testid="contact-details-form"
        @submit="handleAction()"
      >
        <label class="contact-details__inline-input">
          Title
          <SoSelect
            v-model="form.title"
            :disabled="isReadOnly"
            :items="titleOptions"
          />
        </label>
        <label class="contact-details__inline-input">
          First Name*
          <SoTextField
            ref="name"
            v-model="form.forename"
            data-testid="name"
            :disabled="isReadOnly"
            :rules="[rules.required]"
            type="text"
            name="forename"
          />
        </label>
        <label class="contact-details__inline-input">
          Last Name*
          <SoTextField
            v-model="form.surname"
            :disabled="isReadOnly"
            :rules="[rules.required]"
            type="text"
            name="surname"
          />
        </label>
        <label class="contact-details__inline-input">
          Email<template v-if="isEmailRequired">*</template>
          <SoTextField
            v-model="form.email"
            :disabled="isReadOnly"
            :rules="emailRules"
            type="text"
            name="email"
          />
        </label>
        <label class="contact-details__inline-input">
          Phone 1<template v-if="isPrimaryContact">*</template>
          <SoTextField
            v-model="form.phone1"
            :disabled="isReadOnly"
            :rules="isPrimaryContact ? [rules.required, rules.phoneNumber] : []"
            type="text"
            name="phone1"
          />
        </label>
        <label class="contact-details__inline-input">
          Phone 2
          <SoTextField
            v-model="form.phone2"
            :disabled="isReadOnly"
            :rules="[rules.phoneNumber]"
            type="text"
            name="phone2"
          />
        </label>
        <template v-if="!isEdit && form.address">
          <h5 class="mb-4">Address</h5>
          <label class="contact-details__inline-input">
            Line 1*
            <SoTextField
              v-model="form.address.address1"
              :disabled="isReadOnly"
              :rules="[rules.required]"
              type="text"
              name="address1"
            />
          </label>
          <label class="contact-details__inline-input">
            Line 2
            <SoTextField
              v-model="form.address.address2"
              :disabled="isReadOnly"
              type="text"
              name="address2"
            />
          </label>
          <label class="contact-details__inline-input">
            Line 3
            <SoTextField
              v-model="form.address.address3"
              :disabled="isReadOnly"
              type="text"
              name="address3"
            />
          </label>
          <label class="contact-details__inline-input">
            Postcode*
            <SoTextField
              v-model="form.address.postcode"
              :disabled="isReadOnly"
              :rules="[rules.required, rules.postcode]"
              type="text"
              name="postcode"
            />
          </label>
          <label class="contact-details__inline-input">
            Country*
            <SoTextField
              :model-value="address?.countryCode"
              :disabled="isReadOnly"
              readonly
              :rules="[rules.required]"
              type="text"
            />
          </label>
        </template>
        <template #actions>
          <div class="contact-details__actions mt-8">
            <v-spacer />
            <SoButton title="cancel" large @click="closeModal()" />
            <SoButton
              v-if="!isReadOnly"
              :title="isEdit ? 'Save' : 'Create'"
              :disabled="!hasUnSavedChanges || showReservedEmailError"
              :loading="mutators.some((m) => m.isPending)"
              type="success"
              data-testid="contact-details-save-btn"
              large
              submit
            />
          </div>
        </template>
      </SoForm>
    </SoErrorBoundary>
    <SoAlert
      v-if="submitError"
      :title="submitError"
      type="error"
      class="mt-4"
      data-testid="contact-details-error-alert"
    />
  </SoDialog>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import { IContact } from "@/models/contacts/contact.types"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import { IContactForm } from "@/mutators/contact/ContactMutator.types"
  import SoDialog from "@/components/SoDialog.vue"
  import SoErrorBoundary from "@/components/SoErrorBoundary.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoTextField from "@/components/formControls/SoTextField.vue"
  import SoForm from "@/components/formControls/SoForm.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import SoSelect from "@/components/formControls/SoSelect.vue"
  import rules from "@/validators/rules"
  import { getObjectDiff, removeFalsyProperties } from "@/helpers/objectHelpers"
  import { pick } from "lodash-es"
  import { useSnackbarStore } from "@/stores/snackbar"
  import { TITLE_OPTIONS } from "@/constants/titleOptions"
  import { IGdprField } from "@/models/gdprFields/gdprFields.types"
  import { GdprFieldsService } from "@/services/GdprFieldsService"
  import SoLink from "@/components/SoLink.vue"

  const initialFormData = () =>
    ({
      isPrimary: false,
      title: "",
      forename: "",
      surname: "",
      email: "",
      phone1: "",
      phone2: "",
      contactType: "Residential",
      address: {
        address1: "",
        address2: "",
        address3: "",
        address4: null,
        address5: null,
        postcode: "",
      },
    }) as IContactForm

  export default defineComponent({
    components: {
      SoDialog,
      SoErrorBoundary,
      SoButton,
      SoTextField,
      SoForm,
      SoAlert,
      SoSelect,
      SoLink,
    },
    props: {
      modelValue: {
        type: Boolean,
        default: false,
        required: true,
      },
      juniferContactId: {
        type: Number,
        default: undefined,
      },
      gdprFields: {
        type: Array as PropType<IGdprField[]>,
        required: false,
        default: () => [],
      },
    },
    emits: ["update:modelValue"],
    data() {
      return {
        current: useCurrentAccountStore(),
        snackbar: useSnackbarStore(),
        addContactMutator: useCurrentAccountStore().addContact(),
        updateContactMutator: useCurrentAccountStore().updateContact(),
        showAreYouSurePrompt: false,
        rules: rules,
        form: initialFormData(),
        formCopy: initialFormData(),
        titleOptions: TITLE_OPTIONS,
        submitError: "",
      }
    },
    computed: {
      value: {
        get() {
          return this.modelValue
        },
        set(value: boolean) {
          this.$emit("update:modelValue", value)
        },
      },
      isEdit() {
        return this.juniferContactId !== undefined
      },
      isPrimaryContact() {
        return this.current.accountContacts.data.find(
          (contact: IContact) =>
            contact.juniferContactId === this.juniferContactId,
        )?.juniferPrimaryContact
      },
      /*
        SO-32959: an email is mandatory when creating a contact. On edit it
        stays required whenever there is an address to lose — blanking one
        sends `email: ""`, which Mesh rejects during variable coercion with an
        error the agent cannot read. A contact that has never had an email
        stays editable without one, so an agent can still correct its phone
        number.
      */
      isEmailRequired(): boolean {
        return !this.isEdit || !!this.isPrimaryContact || !!this.contact?.email
      },
      emailRules() {
        return this.isEmailRequired
          ? [rules.email, rules.required]
          : [rules.email]
      },
      address() {
        return this.current.account.data.supplyAddress
      },
      contact() {
        return this.current.accountContacts.data.find(
          (contact: IContact) =>
            contact.juniferContactId === this.juniferContactId,
        )
      },
      reservedPaperBillEmails(): string[] {
        return (
          this.current.paperBillState.data?.reservedPaperBillContactEmails ?? []
        )
      },
      isProtectedContact(): boolean {
        return (
          !!this.contact?.email &&
          this.reservedPaperBillEmails.includes(this.contact.email)
        )
      },
      isFormEmailReserved(): boolean {
        const email = this.form.email?.trim().toLowerCase() ?? ""
        if (!email) return false
        return this.reservedPaperBillEmails.some(
          (reserved) => reserved.toLowerCase() === email,
        )
      },
      showReservedEmailError(): boolean {
        return !this.isEdit && this.isFormEmailReserved
      },
      isReadOnly(): boolean {
        return !!this.contact?.isOccupier || this.isProtectedContact
      },
      hasUnSavedChanges() {
        return JSON.stringify(this.form) !== JSON.stringify(this.formCopy)
      },
      mutators() {
        return [this.addContactMutator, this.updateContactMutator]
      },
    },
    watch: {
      value(newVal) {
        if (newVal) {
          this.$nextTick(this.onModalOpen)
        }
      },
    },
    methods: {
      getChangesToUpdate(form: IContactForm): Partial<IContactForm> {
        return pick(form, getObjectDiff(form, this.formCopy))
      },
      onModalOpen() {
        if (this.isEdit && this.contact) {
          this.form = this.parseContact(this.contact)
          this.formCopy = this.parseContact(this.contact)
        } else {
          this.form = {
            ...initialFormData(),
            address: {
              address1: this.address?.addressLine1 || "",
              address2: this.address?.addressLine2 || "",
              address3: this.address?.addressLine3 || "",
              address4: null,
              address5: null,
              postcode: this.address?.postcode || "",
            },
          }
          this.formCopy = initialFormData()
        }
        this.submitError = ""
        this.mutators.forEach((m) => m.reset())
      },
      parseContact(contact: IContact): IContactForm {
        return {
          title: contact.title,
          isPrimary: contact.juniferPrimaryContact,
          forename: contact.firstName,
          surname: contact.lastName,
          email: contact.email,
          phone1: contact.phone1 || "",
          phone2: contact.phone2 || "",
          contactType: "Residential",
          address: {
            address1: this.address?.addressLine1 || "",
            address2: this.address?.addressLine2 || "",
            address3: this.address?.addressLine3 || "",
            address4: null,
            address5: null,
            postcode: this.address?.postcode || "",
          },
        }
      },
      parseContactForm(contact: IContactForm): IContact | undefined {
        if (!this.contact) return undefined

        return {
          ...this.contact,
          title: contact.title || this.contact.title,
          juniferPrimaryContact:
            contact.isPrimary != null
              ? contact.isPrimary
              : this.contact.juniferPrimaryContact,
          firstName: contact.forename || this.contact.firstName,
          lastName: contact.surname || this.contact.lastName,
          email: contact.email || this.contact.email,
          phone1: contact.phone1 || this.contact.phone1,
          phone2: contact.phone2 || this.contact.phone2,
        }
      },
      handleAction() {
        if (this.showReservedEmailError) return
        this.submitError = ""

        if (!this.isEdit) {
          this.addContactMutator
            .mutateAsync(removeFalsyProperties(this.form))
            .then(() => {
              this.snackbar.addSuccess("Successfully created a new contact")
              this.closeModal()
            })
            .catch(this.reportSubmitFailure)
          return
        }

        if (!this.contact) throw new Error("Contact is not specified")

        /*
          Diffed against the untouched form, NOT a falsy-stripped copy: clearing
          a field is a real edit and has to reach the gateway. Only the changed
          fields are sent, so the gateway preserves Junifer's stored value for
          everything the agent did not touch.
        */
        const changesToUpdate = this.getChangesToUpdate(this.form)

        this.updateContactMutator
          .mutateAsync({ contact: this.contact, edits: changesToUpdate })
          .then(() => {
            GdprFieldsService.updateModifiedFields(
              this.gdprFields,
              this.parseContactForm(changesToUpdate),
            )
          })
          .then(() => {
            this.snackbar.addSuccess(
              "Successfully updated customer contact details. It may take some time for the changes to take effect",
            )
            this.closeModal()
          })
          .catch(this.reportSubmitFailure)
      },
      /*
        A gateway Error union member makes the mutator throw, so mutateAsync
        rejects. Without this catch the rejection reaches SoErrorBoundary and the
        agent gets a generic render failure instead of the real reason.
      */
      reportSubmitFailure(error: unknown) {
        this.submitError =
          error instanceof Error
            ? error.message
            : "Something went wrong. Please try again later."
      },
      closeModal() {
        this.submitError = ""
        this.addContactMutator.reset()
        this.updateContactMutator.reset()
        const form = this.$refs.form as typeof SoForm
        form.resetValidation()
        this.value = false
      },
      goToCot() {
        this.$router.push(`/customer/${this.current.accountNumber}/cot`)
        this.closeModal()
      },
    },
  })
</script>

<style scoped>
  .contact-details__actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .contact-details__inline-input {
    display: grid;
    grid-template-columns: 1fr 2fr;
    gap: 8px;
    margin: 4px 0;
    letter-spacing: normal;
  }
</style>
